/**
 * As horas de um evento, como o servidor as aceita — funções puras.
 *
 * Regra do servidor: `start_time` e `end_time` vão os DOIS ou NENHUM (dia
 * inteiro = ambos `null`), e o fim é depois do início (422 caso contrário).
 * Nunca se envia só um: um pedido assim é recusado, e pior, um evento antigo
 * com só uma hora seria movido para um estado inválido.
 */

import { minutesToTime, parseISODate, timeToMinutes } from "../../components/calendar/lib/date-range";

export interface EventTimes {
  start_date: string;
  end_date: string;
  start_time: string | null;
  end_time: string | null;
}

/** Duração que se assume quando um evento antigo só tem hora de início. */
const FALLBACK_DURATION_MIN = 60;

const stamp = (date: string, time: string): number => {
  const d = parseISODate(date);
  const min = timeToMinutes(time) ?? 0;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, min).getTime();
};

export type TimesResult = { ok: true; times: EventTimes } | { ok: false; error: string };

/**
 * Deixa as horas coerentes ou diz porquê não dá.
 * - as duas vazias → dia inteiro (ambas `null`);
 * - só a de início → a de fim é início + 1 h (dados antigos), sem passar da meia-noite;
 * - só a de fim → é ignorado o dia inteiro (não há como saber o início);
 * - o fim tem de ser depois do início.
 */
export function normalizeTimes(input: EventTimes): TimesResult {
  const start = input.start_time?.slice(0, 5) || null;
  let end = input.end_time?.slice(0, 5) || null;

  if (!start && !end) return { ok: true, times: { ...input, start_time: null, end_time: null } };
  if (!start && end) return { ok: false, error: "Indica a hora de início e a de fim, ou nenhuma (dia inteiro)." };
  if (start && !end) end = minutesToTime(Math.min(24 * 60 - 1, (timeToMinutes(start) ?? 0) + FALLBACK_DURATION_MIN));

  if (input.end_date < input.start_date) return { ok: false, error: "A data de fim é anterior à de início." };
  if (stamp(input.end_date, end!) <= stamp(input.start_date, start!)) return { ok: false, error: "A hora de fim tem de ser depois da hora de início." };
  return { ok: true, times: { ...input, start_time: start, end_time: end } };
}

export const MAX_FREE_BUSY_USERS = 50;
export const MAX_RANGE_DAYS = 366;

/** O intervalo cabe no que o servidor aceita em disponibilidade e livre/ocupado (≤ 366 dias)? */
export function withinServerRange(startDate: string, endDate: string): boolean {
  if (!startDate || !endDate) return false;
  const days = Math.round((parseISODate(endDate).getTime() - parseISODate(startDate).getTime()) / 86_400_000);
  return days >= 0 && days <= MAX_RANGE_DAYS;
}

/** Parte uma lista em blocos de `size` (o servidor aceita no máximo 50 pessoas por pergunta). */
export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
